/**
 * Shared task / project type definitions consumed by blocks in
 * `packages/ui/src/blocks/` and by the shell's own server actions and DB
 * layer. Keeping the canonical declarations here lets the blocks stay
 * inside `packages/ui` without reaching into the shell.
 *
 * `excellent/src/projects/types.ts` re-exports these to preserve the
 * shell's `@/projects/types` import path.
 */

// RO-3-02 / RO-4-04: additive subpath, not the bare barrel — see the note in
// `path-label.ts` next to the same fix. This file is on the `work-row.tsx` →
// `GoalsLedger` chain, so a client route rendering `GoalsLedger` pulled the
// whole `@excellent/core` index (and its SQLCipher/keychain tail) in through
// here.
import { HIRING_DISPLAY_NAME } from "@excellent/core/jtbd-registry";

export type TaskStatus =
  | "backlog"
  | "todo"
  | "in_progress"
  | "review"
  | "done"
  | "cancelled";

export type TaskPriority = "low" | "medium" | "high" | "urgent";

export type ProjectSource = "claude" | "manual";

export const STATUS_ORDER: TaskStatus[] = [
  "backlog",
  "todo",
  "in_progress",
  "review",
  "done",
  "cancelled",
];

export const STATUS_LABEL: Record<TaskStatus, string> = {
  backlog: "Backlog",
  todo: "Todo",
  in_progress: "In progress",
  review: "Review",
  done: "Done",
  cancelled: "Cancelled",
};

export const PRIORITY_ORDER: TaskPriority[] = [
  "low",
  "medium",
  "high",
  "urgent",
];

export const PRIORITY_LABEL: Record<TaskPriority, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
};

/**
 * Tone tokens for status + priority — derived from the centralized intent
 * system in `packages/ui/src/tokens/`. Small color swatches/dots only;
 * body text stays `text-foreground` / `text-muted-foreground`.
 *
 * To re-tune a task-status dot color, change the entry in
 * `TASK_STATUS_INTENT` (status.ts) — it ripples here and to every other
 * consumer automatically.
 */
import { INTENT_CLASSES } from "../tokens/intents";
import {
  TASK_PRIORITY_INTENT,
  TASK_STATUS_INTENT,
} from "../tokens/status";

export const STATUS_TONE: Record<TaskStatus, string> = Object.fromEntries(
  (Object.keys(TASK_STATUS_INTENT) as TaskStatus[]).map((status) => [
    status,
    INTENT_CLASSES[TASK_STATUS_INTENT[status]].dot,
  ]),
) as Record<TaskStatus, string>;

export const PRIORITY_TONE: Record<TaskPriority, string> = Object.fromEntries(
  (Object.keys(TASK_PRIORITY_INTENT) as TaskPriority[]).map((priority) => [
    priority,
    INTENT_CLASSES[TASK_PRIORITY_INTENT[priority]].dot,
  ]),
) as Record<TaskPriority, string>;

export interface ProjectRow {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  color: string | null;
  icon: string | null;
  source: ProjectSource;
  group_id: number | null;
  /**
   * Owning workspace (v46). Nullable for forward-compatibility (an older synced
   * peer predating the column emits NULL); read as workspace 1 when null.
   */
  workspace_id: number | null;
  workspace_id_uid: string | null;
  claude_path: string | null;
  claude_slug: string | null;
  /**
   * Codebase role within a parent product (v59). Free-form, e.g. "frontend",
   * "backend", "marketing", "monorepo". Null for standalone/parent projects.
   */
  role: string | null;
  /**
   * Project tree (v63): the parent **product** this project is a codebase of, or
   * null for a standalone project / a product itself. A product's department
   * views aggregate across its children. `parent_id_uid` mirrors the parent's
   * ULID so the edge survives the fleet-sync boundary.
   */
  parent_id: number | null;
  parent_id_uid: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectGroupRow {
  id: number;
  slug: string;
  name: string;
  sort_key: string;
  archived_at: string | null;
  created_at: string;
}

export interface SectionRow {
  id: number;
  project_id: number;
  slug: string;
  name: string;
  sort_key: string;
  archived_at: string | null;
  created_at: string;
}

export interface TaskRow {
  id: number;
  /**
   * ULID sync key (v33) — the identity that crosses the sync boundary, while
   * the integer `id` stays local-only. Every DB-backed row carries it (the
   * scoped reads are `SELECT t.*`); optional because hand-built fixtures and
   * bulk inserts construct rows without it.
   */
  uid?: string;
  project_id: number;
  section_id: number;
  parent_id: number | null;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  assignee: string | null;
  due_date: string | null;
  completed_at: string | null;
  child_count: number;
  sort_key: string;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  /** Optional FK to excellent_goals — goals are folded into Tasks. */
  goal_id: number | null;
  /**
   * Classification columns (v134 / RO-8-03 = W5-A1). Lifted out of title
   * markers so the title can read as a clean human task. Optional on
   * hand-built fixtures; every DB-backed row carries them (human_gated
   * defaults to 0).
   */
  kind?: string | null;
  severity?: string | null;
  human_gated?: number;
  source_ref?: string | null;
}

export interface ScopedTask extends TaskRow {
  /**
   * Server-resolved display label for `assignee` ("Claude (shipper)") —
   * additive, display-only; the raw id stays the identity everywhere.
   * Composed by `getScopedTask`; absent on hand-built fixtures/bulk reads.
   */
  assignee_label?: string | null;
  project_slug: string;
  project_name: string;
  project_color: string | null;
  section_slug: string;
  section_name: string;
  /** Joined from excellent_goals when goal_id is set (for the drawer link). */
  goal_slug: string | null;
  goal_title: string | null;
}

export type ViewScope =
  | { kind: "section"; projectSlug: string; sectionSlug: string }
  | { kind: "project"; projectSlug: string }
  | { kind: "workspace" };

/**
 * Hierarchical task representation built by the shell's DB layer when it
 * needs to render a tree (TaskTree / ProjectHome). The shell exposes
 * `TaskNode` from `@/projects/db`; the canonical definition lives here so
 * blocks in `packages/ui/src/blocks/` can consume it.
 */
export interface TaskNode extends TaskRow {
  children: TaskNode[];
}

export const DEFAULT_SECTIONS: ReadonlyArray<{ slug: string; name: string }> = [
  { slug: "operations", name: "Operations" },
  { slug: "design", name: "Design" },
  { slug: "hiring", name: HIRING_DISPLAY_NAME },
  { slug: "development", name: "Engineering" },
  { slug: "marketing", name: "Marketing" },
];
